import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNotification } from "../common/NotificationContext";
import { InputField } from "../common/InputField";
import { InputFieldRace } from "../common/InputFieldRace";
import Button from "../common/Button";

export const ParticipantsEdit = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const db = getFirestore();
  const auth = getAuth();

  const [formData, setFormData] = useState({
    namaDepan: state?.participant?.namaLengkap?.split(" ")[0] || "",
    namaBelakang:
      state?.participant?.namaLengkap?.split(" ").slice(1).join(" ") || "",
    namaLengkap: state?.participant?.namaLengkap || "",
    email: state?.participant?.email || "",
    alamat: state?.participant?.alamat || "",
    bibName: state?.participant?.bibName || "",
    bibNumber: state?.participant?.bibNumber || "",
    gender: state?.participant?.gender || "",
    golonganDarah: state?.participant?.golonganDarah || "",
    hubunganKontakDarurat: state?.participant?.hubunganKontakDarurat || "",
    kontakDarurat: state?.participant?.kontakDarurat || "",
    kota: state?.participant?.kota || "",
    namaKontakDarurat: state?.participant?.namaKontakDarurat || "",
    negara: state?.participant?.negara || "Indonesia",
    nomorIdentitas: state?.participant?.nomorIdentitas || "",
    provinsi: state?.participant?.provinsi || "",
    sizeJersey: state?.participant?.sizeJersey || "",
    tanggalLahir: state?.participant?.tanggalLahir || "",
    telepon: state?.participant?.telepon || "",
    kategori: state?.participant?.kategori || "",
    orderId: state?.participant?.orderId || "",
    transaction_status: state?.participant?.transaction_status || "",
    harga: state?.participant?.harga || 0,
    distance: state?.participant?.distance || 0,
    performanceIndex: state?.participant?.performanceIndex || "",
    stravaProfile: state?.participant?.stravaProfile || "",
    errors: {}, // Tambahkan errors ke formData
    // Add eventId and collectionName from state if available
    id: state?.participant?.id,
    collectionName: state?.participant?.collectionName,
  });
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [eventId, setEventId] = useState("BMM");
  const [categories, setCategories] = useState([]);

  // Cek status admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          const isAdminUser = idTokenResult.claims.role === "admin";
          console.log("[DEBUG] Admin status in ParticipantsEdit:", isAdminUser);
          setIsAdmin(isAdminUser);
        } catch (err) {
          console.error("[ERROR] Error checking admin status:", err);
          setIsAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, [auth]);

  // Set eventId from navigation state participant data
  useEffect(() => {
    if (state?.participant?.eventId) {
      console.log("[DEBUG] Setting eventId from navigation state:", state.participant.eventId);
      setEventId(state.participant.eventId);
    }
  }, [state]);

  // Fetch categories for the current event (used by edit-only kategori dropdown)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", eventId);
        const eventDoc = await getDoc(eventRef);
        if (!eventDoc.exists()) {
          setCategories([]);
          return;
        }
        const eventData = eventDoc.data();
        const categoriesData = Array.isArray(eventData?.categories)
          ? eventData.categories
          : [];
        setCategories(categoriesData);
      } catch (err) {
        console.error("[ERROR] Error fetching categories:", err);
        setCategories([]);
      }
    };

    if (eventId) fetchCategories();
  }, [db, eventId]);

  // Fetch current user's participant data if no state is provided
  useEffect(() => {
    const fetchCurrentUserParticipantData = async () => {
      // Skip if participant data is already provided via state
      if (state?.participant) {
        console.log("[DEBUG] Participant data provided via state, skipping fetch");
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        console.log("[DEBUG] No authenticated user found");
        return;
      }

      try {
        console.log("[DEBUG] Fetching participant data for user:", user.email);
        
        // First try to find in pendaftaranJMM collection (for JMM events)
        let q = query(collection(db, "pendaftaranJMM"), where("email", "==", user.email));
        let querySnapshot = await getDocs(q);
        let collectionName = "pendaftaranJMM";
        
        // If not found in JMM, try BMM collection
        if (querySnapshot.empty) {
          q = query(collection(db, "pendaftaranBMM"), where("email", "==", user.email));
          querySnapshot = await getDocs(q);
          collectionName = "pendaftaranBMM";
        }
        
        console.log("[DEBUG] Querying collection:", collectionName);
        
        if (!querySnapshot.empty) {
          const participantDoc = querySnapshot.docs[0];
          const participantData = participantDoc.data();
          
          console.log("[DEBUG] Found participant data:", participantData);
          
          // Set eventId from participant data
          if (participantData.eventId) {
            console.log("[DEBUG] Setting eventId from participant data:", participantData.eventId);
            setEventId(participantData.eventId);
          }
          
          // Update formData with fetched data
          setFormData(prev => ({
            ...prev,
            namaDepan: participantData.namaLengkap?.split(" ")[0] || "",
            namaBelakang: participantData.namaLengkap?.split(" ").slice(1).join(" ") || "",
            namaLengkap: participantData.namaLengkap || "",
            email: participantData.email || "",
            alamat: participantData.alamat || "",
            bibName: participantData.bibName || "",
            bibNumber: participantData.bibNumber || "",
            gender: participantData.gender || "",
            golonganDarah: participantData.golonganDarah || "",
            hubunganKontakDarurat: participantData.hubunganKontakDarurat || "",
            kontakDarurat: participantData.kontakDarurat || "",
            kota: participantData.kota || "",
            namaKontakDarurat: participantData.namaKontakDarurat || "",
            negara: participantData.negara || "Indonesia",
            nomorIdentitas: participantData.nomorIdentitas || "",
            provinsi: participantData.provinsi || "",
            sizeJersey: participantData.sizeJersey || "",
            tanggalLahir: participantData.tanggalLahir || "",
            telepon: participantData.telepon || "",
            kategori: participantData.kategori || "",
            orderId: participantData.orderId || "",
            transaction_status: participantData.transaction_status || "",
            harga: participantData.harga || 0,
            distance: participantData.distance || 0,
            performanceIndex: participantData.performanceIndex || "",
            stravaProfile: participantData.stravaProfile || "",
            errors: {},
            // Store document ID and collection name for updates
            id: participantDoc.id,
            collectionName: collectionName,
          }));
          
          console.log("[DEBUG] Updated formData with participant data:", {
            gender: participantData.gender,
            kategori: participantData.kategori,
            allFormData: {
              ...prev,
              namaDepan: participantData.namaLengkap?.split(" ")[0] || "",
              namaBelakang: participantData.namaLengkap?.split(" ").slice(1).join(" ") || "",
              namaLengkap: participantData.namaLengkap || "",
              email: participantData.email || "",
              alamat: participantData.alamat || "",
              bibName: participantData.bibName || "",
              bibNumber: participantData.bibNumber || "",
              gender: participantData.gender || "",
              golonganDarah: participantData.golonganDarah || "",
              hubunganKontakDarurat: participantData.hubunganKontakDarurat || "",
              kontakDarurat: participantData.kontakDarurat || "",
              kota: participantData.kota || "",
              namaKontakDarurat: participantData.namaKontakDarurat || "",
              negara: participantData.negara || "Indonesia",
              nomorIdentitas: participantData.nomorIdentitas || "",
              provinsi: participantData.provinsi || "",
              sizeJersey: participantData.sizeJersey || "",
              tanggalLahir: participantData.tanggalLahir || "",
              telepon: participantData.telepon || "",
              kategori: participantData.kategori || "",
              orderId: participantData.orderId || "",
              transaction_status: participantData.transaction_status || "",
              harga: participantData.harga || 0,
              distance: participantData.distance || 0,
              performanceIndex: participantData.performanceIndex || "",
              stravaProfile: participantData.stravaProfile || "",
              errors: {},
              id: participantDoc.id,
            }
          });
          
          showNotification("success", "Participant data loaded successfully");
        } else {
          console.log("[DEBUG] No participant data found for user:", user.email);
          showNotification("info", "No participant data found for this user");
        }
      } catch (err) {
        console.error("[ERROR] Error fetching participant data:", err);
        showNotification("error", "Failed to load participant data. Please try again.");
      }
    };

    fetchCurrentUserParticipantData();
  }, [state, auth, db, showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Update namaLengkap when namaDepan or namaBelakang changes
      if (name === "namaDepan" || name === "namaBelakang") {
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
      }
      // Update namaDepan and namaBelakang when namaLengkap changes
      if (name === "namaLengkap") {
        const namaSplit = value.trim().split(/\s+/);
        updated.namaDepan = namaSplit[0] || "";
        updated.namaBelakang = namaSplit.slice(1).join(" ") || "";
      }
      // Update harga and distance when kategori changes
      if (name === "kategori") {
        const selectedCategory = categories.find(
          (cat) => cat.categoryName === value
        );
        if (selectedCategory) {
          updated.distance = selectedCategory.distance || 0;
          const nextHarga =
            prev.negara === "Indonesia"
              ? selectedCategory.price
              : selectedCategory.foreignerPrice;
          updated.harga = typeof nextHarga === "number" ? nextHarga : 0;
        } else {
          updated.distance = 0;
          updated.harga = 0;
        }
        // Hapus error kategori untuk admin
        if (isAdmin) {
          updated.errors = { ...updated.errors, kategori: undefined };
        }
      }
      // Update errors jika disediakan
      if (name === "errors") {
        updated.errors = value;
      }

      return updated;
    });
  };

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    if (str === "bibNumber") return "BIB Number";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const validateFormat = (field, value, negara = "Indonesia") => {
    const label = humanize(field);

    // Optional fields (match InputFieldRace behavior)
    if (
      (field === "performanceIndex" || field === "stravaProfile") &&
      (value === undefined || value === null || value.toString().trim() === "")
    ) {
      return "";
    }

    // Allow empty bibNumber
    if (
      field === "bibNumber" &&
      (value === undefined || value === null || value === "")
    ) {
      return "";
    }

    // Validate required fields
    if (field !== "bibNumber" && (!value || value.trim() === "")) {
      return `${label} is required`;
    }

    if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return `${label} must be a valid email address, e.g. user@example.com`;
    }
    if (field === "kategori") {
      if (!categories.some((cat) => cat?.categoryName === value)) {
        return `${label} must be selected from the available list.`;
      }
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation: keep in sync with /form/JMM (InputFieldRace)
    // Only validate edit-only fields here; InputFieldRace handles the rest.
    const errors = {};
    const emailError = validateFormat("email", formData.email, formData.negara);
    if (emailError) errors.email = emailError;
    const kategoriError = validateFormat("kategori", formData.kategori, formData.negara);
    if (kategoriError) errors.kategori = kategoriError;

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showNotification("error", firstError);
      setFormData((prev) => ({ ...prev, errors }));
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Unauthorized");

      const idToken = await user.getIdToken();

      const participantId = state?.participant?.id || formData.id;
      const collectionName = state?.participant?.collectionName || formData.collectionName || null;

      if (!participantId) {
        throw new Error("Participant ID not found");
      }

      const apiBase = import.meta.env.VITE_API_BASE_URL;
      if (!apiBase) {
        throw new Error("VITE_API_BASE_URL is missing");
      }

      const resp = await fetch(
        `${apiBase}/api/admin/participants/${participantId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            collectionName,
            orderId: formData.orderId || null,
            eventId: eventId || null,
            data: {
              namaDepan: formData.namaDepan,
              namaBelakang: formData.namaBelakang,
              namaLengkap: formData.namaLengkap,
              email: formData.email,
              telepon: formData.telepon,
              nomorIdentitas: formData.nomorIdentitas,
              tanggalLahir: formData.tanggalLahir,
              gender: formData.gender,
              negara: formData.negara,
              provinsi: formData.provinsi,
              kota: formData.kota,
              alamat: formData.alamat,
              kontakDarurat: formData.kontakDarurat,
              namaKontakDarurat: formData.namaKontakDarurat,
              hubunganKontakDarurat: formData.hubunganKontakDarurat,
              bibName: formData.bibName,
              bibNumber: formData.bibNumber || "",
              golonganDarah: formData.golonganDarah,
              sizeJersey: formData.sizeJersey,
              performanceIndex: formData.performanceIndex,
              stravaProfile: formData.stravaProfile,
              kategori: formData.kategori,
              harga: formData.harga,
              distance: formData.distance,
            },
          }),
        }
      );

      if (!resp.ok) {
        let msg = "Failed to update participant";
        try {
          const data = await resp.json();
          msg = data?.error || data?.message || msg;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      showNotification("success", "Participant data updated successfully.");
      navigate("/admin/Participants");
    } catch (err) {
      console.error("[ERROR] Error updating participant:", err);
      showNotification(
        "error",
        "Failed to update participant data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/Participants");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white shadow-xl px-4 sm:px-6 lg:px-8 z-0">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 py-20 overflow-visible">
        <div className="w-full md:w-3/4 max-w-4xl bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md">
          <h2 className="text-2xl font-semibold text-left mb-4">
            Edit Participant
          </h2>
          <form id="form-edit-peserta" onSubmit={handleSubmit}>
            <InputFieldRace
              formData={formData}
              handleChange={handleChange}
              isFormDisabled={loading}
              labelColor="text-slate-800"
              eventId={eventId}
              categoryName={formData.kategori}
            />

            <div className="flex gap-4">
              <div className="flex-1">
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => handleChange(e)}
                  disabled={loading}
                  required
                  labelColor="text-slate-800"
                />
              </div>
              <div className="flex-1">
                <InputField
                  label="Kategori"
                  name="kategori"
                  type="select"
                  value={formData.kategori || ""}
                  onChange={(e) => handleChange(e)}
                  disabled={loading}
                  required
                  options={[
                    { value: "", label: "Pilih Kategori" },
                    ...categories
                      .map((cat) => cat?.categoryName)
                      .filter(Boolean)
                      .map((name) => ({ value: name, label: name })),
                  ]}
                  labelColor="text-slate-800"
                />
              </div>
            </div>
          </form>
        </div>

        <aside className="w-full md:w-1/4 max-w-sm bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4 flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-left px-4 md:px-0">
            Order Information
          </h2>

          <div className="text-lg text-left space-y-2 mt-2 px-4 md:px-0">
            <p className="text-sm">
              Category: <strong>{formData.kategori || "N/A"}</strong>
            </p>
            <p className="text-sm">
              Order ID: <strong>{formData.orderId || "N/A"}</strong>
            </p>
            <p className="text-sm">
              Transaction Status:{" "}
              <strong>{formData.transaction_status || "N/A"}</strong>
            </p>
            <p className="text-sm">
              Price:{" "}
              <strong>
                Rp {(typeof formData.harga === "number" ? formData.harga : 0).toLocaleString()}
              </strong>
            </p>
            <p className="text-sm">
              Distance:{" "}
              <strong>
                {formData.distance ? `${formData.distance} KM` : "N/A"}
              </strong>
            </p>
          </div>

          <div className="px-4 md:px-0 w-full">
            <hr className="border-t border-gray-200 my-3" />
            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                form="form-edit-peserta"
                fullWidth
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                onClick={handleCancel}
                fullWidth
                disabled={loading}
                className="!bg-gray-200 !text-gray-800 hover:!bg-gray-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};
